'use memo';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import IconButton from '@/components/Button/IconButton';
import Combobox from '@/components/Combobox';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useBankBranches } from './useBankBranches';
import type { CodeNameEntry } from './useBanks';
const BRANCH_CODE_PATTERN = /^\d{3}$/;
export interface SelectBankBranchProps {
  bankCode?: string;
  value?: CodeNameEntry;
  onChange: (entry?: CodeNameEntry) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  rootClassName?: string;
  width?: number;
  triggerRef?: React.Ref<HTMLButtonElement>;
}
const SelectBankBranch = ({
  bankCode,
  value,
  onChange,
  allowEmpty,
  disabled,
  rootClassName,
  width,
  triggerRef
}: SelectBankBranchProps) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.selectBranch'
  });
  const [open, setOpen] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const popoverContentId = useId();
  const {
    bankBranches,
    isLoading,
    error
  } = useBankBranches(bankCode);
  const autoConfirmedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  // oxlint-disable-next-line react-hooks-js/refs -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
  onChangeRef.current = onChange;
  const prevBankCodeRef = useRef(bankCode);

  // oxlint-disable-next-line react-hooks-js/refs -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
  if (prevBankCodeRef.current !== bankCode) {
    // oxlint-disable-next-line react-hooks-js/refs -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
    prevBankCodeRef.current = bankCode;
    setRawInput('');
    // oxlint-disable-next-line react-hooks-js/refs -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
    autoConfirmedRef.current = false;
  }
  const options = useMemo(() => bankBranches.map(entry => ({
    value: entry.code,
    label: `${entry.name} (${entry.code})`,
    searchKeywords: entry.searchKeywords
  })), [bankBranches]);
  const canClear = Boolean(allowEmpty && !disabled && value);
  const isBankSelected = !!bankCode;
  const displayLabel = value ? `${value.name} (${value.code})` : isBankSelected ? t('placeholder') : t('selectBankFirst');
  const handleValueChange = (code: string | null) => {
    if (code) {
      const entry = bankBranches.find(b => b.code === code);
      if (entry) {
        onChange(entry);
      }
    } else {
      onChange(undefined);
    }
    setOpen(false);
  };
  const handleSearchChange = (text: string) => {
    setRawInput(text);
    autoConfirmedRef.current = false;
  };
  useEffect(() => {
    if (!open || !rawInput || isLoading || autoConfirmedRef.current || !BRANCH_CODE_PATTERN.test(rawInput)) {
      return;
    }
    const exactMatch = bankBranches.filter(b => b.code === rawInput);
    if (exactMatch.length !== 1) {
      return;
    }
    autoConfirmedRef.current = true;
    onChangeRef.current(exactMatch[0]);
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
    setOpen(false);
  }, [bankBranches, rawInput, open, isLoading]);
  return <>
      <Popover open={open} onOpenChange={newOpen => {
      setOpen(newOpen);
      if (newOpen) {
        setRawInput('');
        autoConfirmedRef.current = false;
      }
    }}>
        <div className={cn('relative inline-block', rootClassName)} style={rootClassName ? undefined : {
        width: width ?? 280
      }}>
          <PopoverTrigger asChild>
            <Button ref={triggerRef} variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverContentId} className="relative w-full justify-start pr-10" disabled={disabled || !isBankSelected}>
              <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
                {displayLabel}
              </span>
              <div className="absolute inset-y-0 right-3 flex items-center">
                <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
              <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label={t('clearSelection')} onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onChange(undefined);
            setOpen(false);
            setRawInput('');
          }} onPointerDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }} />
            </div>}
        </div>
        <PopoverContent id={popoverContentId} className="w-72 p-0">
          <Combobox type="single" options={options} value={value?.code ?? ''} onValueChange={handleValueChange} search={rawInput} isFetching={isLoading} onSearchChange={handleSearchChange} searchPlaceholder={t('searchPlaceholder')} noResultsMessage={t('noResultsMessage')} showCheckbox={false} />
        </PopoverContent>
      </Popover>
      {error && <Text variant="labelSmall" className="mt-1 text-error-on-neutral-fill">
          {t('fetchError')}
        </Text>}
    </>;
};
export default SelectBankBranch;